import React, { useState, useEffect, useCallback } from 'react';
import { Process, ProcessCreate, ProcessUpdate, CurrencyLookupItem } from '../types/costwand.d';
import { Input } from './ui/input.tsx';
import { Button } from './ui/button.tsx';
import { Label } from '@radix-ui/react-label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.tsx';
import { Spinner } from './ui/spinner.tsx'; // Import Spinner

interface ProcessFormProps {
  initialData?: Process | null;
  onSubmit: (data: ProcessCreate | ProcessUpdate) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  // error?: string | null; // Removed error prop
  currencies: CurrencyLookupItem[];
  lookupLoading: boolean;
}

const defaultFormData: ProcessCreate = {
  description: '',
  currency_id: null as any, // Changed from 0 to null
  cost: 0.0,
  created_by: 'tester', // Default value for created_by
};

const ProcessForm: React.FC<ProcessFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  // error, // Removed error from destructuring
  currencies,
  lookupLoading,
}) => {
  const [formData, setFormData] = useState<ProcessCreate | ProcessUpdate>(
    initialData
      ? {
          description: initialData.description,
          currency_id: initialData.currency_id || null, // Ensure null if 0 or undefined
          cost: initialData.cost,
          updated_by: initialData.updated_by || 'tester', // Use existing or default
        }
      : defaultFormData
  );

  useEffect(() => {
    if (initialData) {
      setFormData({
        description: initialData.description,
        currency_id: initialData.currency_id || null,
        cost: initialData.cost,
        updated_by: initialData.updated_by || 'tester',
      });
    } else {
      setFormData(defaultFormData);
    }
  }, [initialData]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: type === 'number' ? parseFloat(value) || 0 : value,
    }));
  }, []);

  const handleSelectChange = useCallback((id: keyof (ProcessCreate | ProcessUpdate), value: string) => {
    setFormData((prev) => ({
      ...prev,
      [id]: value === '' ? null : parseInt(value, 10), // Handle empty string for 'None'
    }));
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  }, [formData, onSubmit]);

  const isDisabled = isSubmitting || lookupLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* {error && <p className="text-destructive text-sm">{error}</p>} */} {/* Removed local error display */}
      <div>
        <Label htmlFor="description" className="block text-sm font-medium text-foreground mb-1">Description</Label>
        <Input
          id="description"
          type="text"
          required
          value={formData.description || ''}
          onChange={handleChange}
          disabled={isDisabled}
        />
      </div>
      <div>
        <Label htmlFor="cost" className="block text-sm font-medium text-foreground mb-1">Cost</Label>
        <Input
          id="cost"
          type="number"
          step="0.01"
          required
          value={formData.cost || ''}
          onChange={handleChange}
          disabled={isDisabled}
        />
      </div>
      <div>
        <Label htmlFor="currency_id" className="block text-sm font-medium text-foreground mb-1">Currency</Label>
        <Select
          onValueChange={(value) => handleSelectChange('currency_id', value)}
          value={formData.currency_id ? String(formData.currency_id) : ''}
          disabled={isDisabled}
          required
        >
          <SelectTrigger id="currency_id">
            <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading currencies...</span> : 'Select a currency'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="nil" disabled> {/* Added for required field placeholder */}
              {lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading currencies...</span> : 'Select a currency'}
            </SelectItem>
            {currencies.map((item) => (
              <SelectItem key={item.id} value={String(item.id)}>
                {item.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="submit" disabled={isDisabled}>
          {isSubmitting ? <span className="flex items-center gap-2"><Spinner size="sm" /> Saving...</span> : 'Save'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isDisabled}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default ProcessForm;